import { useState } from "react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { CheckCircle, Leaf, Award, Globe, ShieldCheck, ArrowRight, ChevronDown } from "lucide-react";
import { submitWholesaleInquiry, getClientId } from "./supabase";
import { TurnstileWidget } from "./TurnstileWidget";
import { toast } from "sonner";
import { useAdminData, type WholesaleConfig } from "./admin/AdminDataContext";
import { useTheme } from "./ThemeInjector";
import { localized } from "./admin/TranslationsPanel";
import { useLanguage } from "./LanguageContext";

const CERT_ICONS = [Leaf, Award, Globe, ShieldCheck];

/** Normalize features to string[] whether stored as string (newline-sep) or array. */
function toFeaturesArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((f) => typeof f === "string" && f.trim());
  if (typeof value === "string") return value.split("\n").map((f) => f.trim()).filter(Boolean);
  return [];
}

/** Return Turnstile theme from page background: dark bg → dark widget, light bg → light. */
function turnstileThemeFromBg(hex: string): "light" | "dark" {
  const h = hex.replace(/^#/, "");
  if (h.length !== 6 && h.length !== 8) return "light";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 0.5 ? "dark" : "light";
}

export function WholesalePage() {
  const { siteConfig } = useAdminData();
  const theme = useTheme();
  const { lang } = useLanguage();
  const ws: WholesaleConfig = siteConfig.wholesale || {
    hero: { label: "Wholesale Program", title: "Exclusive Grade Matcha", subtitle: "", image: "" },
    description: { label: "", title: "", text: "" },
    showcase: { label: "", title: "", features: [], image: "", cta_text: "Request Samples" },
    tiers: [],
    faqs: [],
    inquiry: { label: "Get in Touch", title: "Wholesale Inquiry", subtitle: "" },
    certifications: [],
  };

  const [formData, setFormData] = useState({
    company: "",
    name: "",
    email: "",
    volume: "",
    message: "",
    partnership_tier: "",
    website: "", // honeypot
  });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [turnstileErrorOccurred, setTurnstileErrorOccurred] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [submitLocked, setSubmitLocked] = useState(false);

  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITEKEY) as string | undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast.error("Please complete the verification check.");
      return;
    }
    if (submitLocked) {
      toast.error("Too many submissions. Please wait a few minutes.");
      return;
    }
    if (submitCount >= 3) {
      setSubmitLocked(true);
      toast.error("Too many submissions. Please wait 5 minutes before trying again.");
      setTimeout(() => { setSubmitLocked(false); setSubmitCount(0); }, 5 * 60 * 1000);
      return;
    }

    setSubmitting(true);
    const result = await submitWholesaleInquiry({
      company_name: formData.company.trim().slice(0, 200),
      contact_name: formData.name.trim().slice(0, 100),
      email: formData.email.trim().slice(0, 254),
      estimated_volume: formData.volume?.trim() || undefined,
      message: formData.message?.trim() ? formData.message.trim().slice(0, 2000) : undefined,
      partnership_tier: formData.partnership_tier?.trim() || undefined,
      website: formData.website || undefined,
      client_id: getClientId(),
      turnstile_token: turnstileToken,
    });
    setSubmitting(false);
    setSubmitCount((c) => c + 1);

    if (!result.ok) {
      toast.error(result.error || "Something went wrong. Please try again or contact us directly.");
    } else {
      toast.success("Thank you for your inquiry. Our wholesale team will be in touch within 24 hours.");
      setFormData({ company: "", name: "", email: "", volume: "", message: "", partnership_tier: "", website: "" });
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px]">
        <ImageWithFallback
          src={ws.hero.image}
          alt="Wholesale hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${theme.fg}80, ${theme.fg}4d, ${theme.bg})` }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p
            className="tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            {localized(ws.hero, "label", lang)}
          </p>
          <h1
            className="text-white tracking-[0.15em] mb-4 drop-shadow-lg"
            style={{ fontFamily: theme.fontHeading, fontSize: "clamp(32px, 5vw, 60px)", fontWeight: 300 }}
          >
            {localized(ws.hero, "title", lang)}
          </h1>
          <p
            className="text-white/80 max-w-xl tracking-[0.05em] drop-shadow-md"
            style={{ fontFamily: theme.fontBody, fontSize: "clamp(14px, 2vw, 17px)", fontWeight: 300 }}
          >
            {localized(ws.hero, "subtitle", lang)}
          </p>
        </div>
      </section>

      {/* Why Partner Section */}
      {ws.description.title && (
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p
              className="tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
            >
              {localized(ws.description, "label", lang)}
            </p>
            <h2
              className="tracking-[0.1em] mb-6"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400, color: theme.fg }}
            >
              {localized(ws.description, "title", lang)}
            </h2>
            <p
              className="max-w-2xl mx-auto leading-[1.8]"
              style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
            >
              {localized(ws.description, "text", lang)}
            </p>
          </div>

          {ws.certifications.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {ws.certifications.map((cert, i) => {
                const Icon = CERT_ICONS[i % CERT_ICONS.length];
                return (
                  <div
                    key={i}
                    className="text-center p-8 rounded-lg shadow-[0_2px_20px_rgba(0,0,0,0.03)]"
                    style={{ backgroundColor: theme.card }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5"
                      style={{ backgroundColor: `${theme.primary}1a` }}
                    >
                      <Icon size={22} style={{ color: theme.primary }} />
                    </div>
                    <h3
                      className="tracking-[0.05em] mb-2"
                      style={{ fontFamily: theme.fontHeading, fontSize: "20px", fontWeight: 500, color: theme.fg }}
                    >
                      {localized(cert, "title", lang)}
                    </h3>
                    <p
                      style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.muted }}
                    >
                      {localized(cert, "desc", lang)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Product Showcase */}
      {ws.showcase.title && (
        <section className="py-24" style={{ backgroundColor: `${theme.border}40` }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="overflow-hidden rounded-lg">
                <ImageWithFallback
                  src={ws.showcase.image}
                  alt="Product showcase"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div>
                <p
                  className="tracking-[0.3em] uppercase mb-4"
                  style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
                >
                  {localized(ws.showcase, "label", lang)}
                </p>
                <h2
                  className="tracking-[0.08em] mb-8"
                  style={{ fontFamily: theme.fontHeading, fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, color: theme.fg }}
                >
                  {localized(ws.showcase, "title", lang)}
                </h2>
                <div className="space-y-5 mb-10">
                  {(() => {
                    const featuresRaw = localized(ws.showcase, "features", lang);
                    const defaultFeatures = toFeaturesArray(ws.showcase.features);
                    const features = toFeaturesArray(featuresRaw).length > 0 ? toFeaturesArray(featuresRaw) : defaultFeatures;
                    return features.map((item: string) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle size={18} className="mt-0.5 shrink-0" style={{ color: theme.primary }} />
                        <p
                          style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
                        >
                          {item}
                        </p>
                      </div>
                    ));
                  })()}
                </div>
                <a
                  href="#inquiry"
                  className="inline-flex items-center gap-2 px-8 py-3 text-white tracking-[0.2em] uppercase transition-all duration-500"
                  style={{ fontFamily: theme.fontBody, fontSize: "12px", fontWeight: 400, backgroundColor: theme.primary, borderRadius: theme.btnRadius }}
                >
                  {localized(ws.showcase, "cta_text", lang)}
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Tiers */}
      {ws.tiers.length > 0 && (
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p
              className="tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
            >
              Partnership Tiers
            </p>
            <h2
              className="tracking-[0.1em]"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400, color: theme.fg }}
            >
              Wholesale Pricing
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ws.tiers.map((tier) => (
              <div
                key={tier.name}
                className="rounded-lg p-8 transition-all duration-500 hover:-translate-y-1"
                style={{
                  backgroundColor: tier.featured ? theme.fg : theme.card,
                  color: tier.featured ? theme.bg : theme.fg,
                  boxShadow: tier.featured
                    ? "0 8px 40px rgba(0,0,0,0.15)"
                    : "0 2px 20px rgba(0,0,0,0.04)",
                }}
              >
                {tier.featured && (
                  <p
                    className="tracking-[0.3em] uppercase mb-4"
                    style={{ fontFamily: theme.fontBody, fontSize: "10px", color: theme.accent }}
                  >
                    Most Popular
                  </p>
                )}
                <h3
                  className="tracking-[0.1em] mb-2"
                  style={{ fontFamily: theme.fontHeading, fontSize: "28px", fontWeight: 500 }}
                >
                  {localized(tier, "name", lang)}
                </h3>
                <p
                  className="tracking-[0.1em] mb-1"
                  style={{ fontFamily: theme.fontBody, fontSize: "24px", fontWeight: 400, color: theme.accent }}
                >
                  {localized(tier, "priceRange", lang)}
                </p>
                <p
                  className="tracking-[0.1em] mb-8"
                  style={{
                    fontFamily: theme.fontBody,
                    fontSize: "13px",
                    fontWeight: 300,
                    color: tier.featured ? `${theme.bg}80` : theme.muted,
                  }}
                >
                  Min. order: {localized(tier, "minOrder", lang)}
                </p>
                <div className="space-y-3 mb-8">
                  {(() => {
                    const featuresRaw = localized(tier, "features", lang);
                    const defaultFeatures = toFeaturesArray(tier.features);
                    const features = toFeaturesArray(featuresRaw).length > 0 ? toFeaturesArray(featuresRaw) : defaultFeatures;
                    return features.map((feature: string) => (
                      <div key={feature} className="flex items-start gap-3">
                        <CheckCircle
                          size={16}
                          className="mt-0.5 shrink-0"
                          style={{ color: tier.featured ? theme.accent : theme.primary }}
                        />
                        <p
                          style={{
                            fontFamily: theme.fontBody,
                            fontSize: "14px",
                            fontWeight: 300,
                            color: tier.featured ? `${theme.bg}cc` : theme.muted,
                          }}
                        >
                          {feature}
                        </p>
                      </div>
                    ));
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, partnership_tier: tier.name }));
                    document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="block w-full py-3 text-center tracking-[0.2em] uppercase transition-all duration-500"
                  style={{
                    fontFamily: theme.fontBody,
                    fontSize: "11px",
                    fontWeight: 400,
                    borderRadius: theme.btnRadius,
                    ...(tier.featured
                      ? { backgroundColor: theme.accent, color: "#fff" }
                      : { border: `1px solid ${theme.primary}`, color: theme.primary }),
                  }}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {ws.faqs.length > 0 && (
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p
              className="tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
            >
              Frequently Asked Questions
            </p>
            <h2
              className="tracking-[0.1em]"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400, color: theme.fg }}
            >
              Common Queries
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {ws.faqs.map((faq, index) => (
              <div
                key={index}
                className="rounded-lg shadow-[0_2px_20px_rgba(0,0,0,0.03)] overflow-hidden"
                style={{ backgroundColor: theme.card }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-7 py-5 text-left"
                >
                  <h3
                    className="tracking-[0.05em] pr-4"
                    style={{ fontFamily: theme.fontHeading, fontSize: "20px", fontWeight: 500, color: theme.fg }}
                  >
                    {localized(faq, "q", lang)}
                  </h3>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`}
                    style={{ color: theme.accent }}
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-out"
                  style={{
                    maxHeight: openFaq === index ? "300px" : "0px",
                    opacity: openFaq === index ? 1 : 0,
                  }}
                >
                  <p
                    className="px-7 pb-6 leading-[1.8]"
                    style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                  >
                    {localized(faq, "a", lang)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Inquiry Form */}
      <section id="inquiry" className="py-24" style={{ backgroundColor: `${theme.border}40` }}>
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <p
              className="tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
            >
              {localized(ws.inquiry, "label", lang)}
            </p>
            <h2
              className="tracking-[0.1em] mb-4"
              style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 38px)", fontWeight: 400, color: theme.fg }}
            >
              {localized(ws.inquiry, "title", lang)}
            </h2>
            <p
              style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
            >
              {localized(ws.inquiry, "subtitle", lang)}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot: hidden from users, bots often fill it */}
            <div
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                  Company Name
                </label>
                <input type="text" required value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  maxLength={200}
                  className="w-full px-4 py-3 border focus:outline-none transition-colors"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: theme.btnRadius }}
                  placeholder="Your business name" />
              </div>
              <div>
                <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                  Contact Name
                </label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={100}
                  className="w-full px-4 py-3 border focus:outline-none transition-colors"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: theme.btnRadius }}
                  placeholder="Your full name" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                  Email
                </label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  maxLength={254}
                  className="w-full px-4 py-3 border focus:outline-none transition-colors"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: theme.btnRadius }}
                  placeholder="you@company.com" />
              </div>
              <div>
                <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                  Estimated Volume
                </label>
                <select value={formData.volume} onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                  className="w-full px-4 py-3 border focus:outline-none transition-colors appearance-none"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: theme.btnRadius }}
                >
                  <option value="">Select volume</option>
                  <option value="5-25kg">5 - 25 kg / month</option>
                  <option value="25-100kg">25 - 100 kg / month</option>
                  <option value="100kg+">100+ kg / month</option>
                </select>
              </div>
            </div>
            {ws.tiers.length > 0 && (
              <div>
                <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                  Partnership tier
                </label>
                <select
                  value={formData.partnership_tier}
                  onChange={(e) => setFormData({ ...formData, partnership_tier: e.target.value })}
                  className="w-full px-4 py-3 border focus:outline-none transition-colors appearance-none"
                  style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: theme.btnRadius }}
                >
                  <option value="">Select tier</option>
                  {ws.tiers.map((tier) => (
                    <option key={tier.name} value={tier.name}>
                      {localized(tier, "name", lang)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block tracking-[0.15em] uppercase mb-2" style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 400, color: theme.muted }}>
                Message
              </label>
              <textarea rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                maxLength={2000}
                className="w-full px-4 py-3 border focus:outline-none transition-colors resize-none"
                style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg, borderRadius: "8px" }}
                placeholder="Tell us about your business, goals, and how we can help..." />
            </div>
            {turnstileSiteKey ? (
              <div className="space-y-2">
                <TurnstileWidget
                  key={turnstileKey}
                  siteKey={turnstileSiteKey}
                  theme={turnstileThemeFromBg(theme.bg)}
                  onVerify={(token) => {
                    setTurnstileToken(token);
                    setTurnstileErrorOccurred(false);
                  }}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => {
                    setTurnstileToken(null);
                    setTurnstileErrorOccurred(true);
                    toast.error("Verification failed (e.g. network or browser). Use \"Retry verification\" below or refresh the page.");
                  }}
                />
                {turnstileErrorOccurred && (
                  <button
                    type="button"
                    onClick={() => {
                      setTurnstileErrorOccurred(false);
                      setTurnstileKey((k) => k + 1);
                    }}
                    className="text-sm text-[#6F7F63] hover:text-[#5a6b4f] underline focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded"
                    style={{ fontFamily: theme.fontBody }}
                  >
                    Retry verification
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm" style={{ color: theme.muted }}>
                Verification is not configured. Please set VITE_TURNSTILE_SITE_KEY to enable the form.
              </p>
            )}
            <button type="submit"
              disabled={submitting || submitLocked || !turnstileToken}
              className="w-full py-4 text-white tracking-[0.25em] uppercase transition-all duration-500 disabled:opacity-50"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", fontWeight: 400, backgroundColor: theme.accent, borderRadius: theme.btnRadius }}
            >
              {submitting ? "Submitting..." : "Submit Inquiry"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}